import { Box, Button, Divider, styled, Tooltip, Typography } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useFetchData } from '../../../../hooks/useFetchData';
import { useTestId } from '../../../../hooks/useTestId';
import { processQuestion, recalculateBenchmarks } from '../../../../services/dealGPT';
import { ProjectRunAnalysisQuestion, ProjectSalesforceInfo } from '../../../../Types/dealGPT';
import { AnalysisQuestionStatus, ProcessReason } from '../../../../Types/enums';
import {
  getBenchmarkCategoryGroups,
  getLatestQuestionVersions,
} from '../../../../util/benchmarkUtils';
import { projectSalesforceInfoPathUrl } from '../../../../util/routesUrls';
import LoadingMask from '../../../LoadingMask';
import { AnalyticsEvent, useAnalytics } from '../../../Providers/AnalyticsProvider';
import { useNotifications } from '../../../Providers/NotificationsProvider';
import ApplyChangesDialog from '../ApplyChangesDialog';
import Benchmark from './Benchmark';

const FixedWidthTypography = styled(Typography)({
  width: '130px',
  color: '#50658E',
});

const TruncatedBlueText = styled(Typography)({
  color: '#0047FF',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  padding: '0 5px',
  width: '120px',
  maxWidth: '120px',
});

interface BenchmarkPageProps {
  projectId: string;
  scope: string | undefined;
  pageQuestions: ProjectRunAnalysisQuestion[];
  setShouldFetchQuestions: React.Dispatch<React.SetStateAction<boolean>>;
}

const BenchmarkPage: React.FC<BenchmarkPageProps> = ({
  projectId,
  scope,
  pageQuestions,
  setShouldFetchQuestions,
}) => {
  const analytics = useAnalytics();
  const [openDialog, setOpenDialog] = useState(false);
  const { data: salesforce, loading } = useFetchData<ProjectSalesforceInfo>({
    urlPath: projectSalesforceInfoPathUrl(projectId),
  });
  const [processing, setProcessing] = useState(false);
  const tid = useTestId('benchmark-page');
  const { refetchNotifications } = useNotifications();

  const runId = useMemo(() => pageQuestions[0]?.runId, [pageQuestions]);

  const latestQuestions = useMemo(() => getLatestQuestionVersions(pageQuestions), [pageQuestions]);

  const groupedBenchmarks = useMemo(
    () => getBenchmarkCategoryGroups(latestQuestions),
    [latestQuestions]
  );

  const createdAtDate = useMemo(() => {
    const initialQ = pageQuestions.find((q) => q.status === AnalysisQuestionStatus.INITIAL);
    if (!initialQ) return null;

    return new Date(initialQ.createdAt).toLocaleDateString();
  }, [pageQuestions]);

  const unprocessedQuestion = useMemo(
    () => latestQuestions.find((p) => !!p.unprocessedDownstreamChanges),
    [latestQuestions]
  );

  const refetchQuestions = useCallback(
    () => setShouldFetchQuestions(true),
    [setShouldFetchQuestions]
  );

  const handleRefresh = useCallback(async () => {
    try {
      analytics.event(
        AnalyticsEvent.WM_IA_A_BENCHMARK_REFRESH,
        { id: projectId, value: `RunId=${runId},Scope=${scope}` },
        { runId, projectId, scope }
      );
      setProcessing(true);
      await recalculateBenchmarks(projectId, runId);
      refetchQuestions();
      setProcessing(false);
    } catch (error) {
      enqueueSnackbar('Failed to refresh benchmarks. Please try again later.', {
        variant: 'error',
      });
      return;
    }
  }, [projectId, runId, scope, refetchQuestions, analytics]);

  const handleConfirmApply = useCallback(async () => {
    if (!unprocessedQuestion) return;
    try {
      analytics.event(
        AnalyticsEvent.WM_IA_A_BENCHMARK_APPLY,
        { id: projectId, value: `RunId=${runId},Scope=${scope}` },
        { runId, projectId, scope }
      );
      setOpenDialog(false);
      setProcessing(true);
      enqueueSnackbar('Applying changes to downstream dependencies.', { variant: 'info' });
      await processQuestion(unprocessedQuestion, ProcessReason.APPLY_CHANGES);
      refetchNotifications();
      refetchQuestions();
      refetchNotifications();
    } catch (error) {
      enqueueSnackbar('Failed to apply changes. Please try again later.', {
        variant: 'error',
      });
    } finally {
      setProcessing(false);
    }
  }, [
    projectId,
    runId,
    scope,
    unprocessedQuestion,
    refetchQuestions,
    analytics,
    refetchNotifications,
  ]);

  useEffect(() => {
    analytics.event(
      AnalyticsEvent.WM_IA_A_BENCHMARK_PAGE_LOAD,
      { id: projectId, value: `RunId=${runId},Scope=${scope}` },
      { runId, projectId, scope }
    );
  }, [projectId, runId, scope, analytics]);

  return (
    <Box>
      <Box
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 3, mb: 2 }}
      >
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Typography sx={{ color: '#50658E' }}>Benchmark data from:</Typography>
          {createdAtDate && <Typography>{createdAtDate}</Typography>}
        </Box>
        <Tooltip title="This will recalculate benchmarks and reset all selections to default">
          <Button
            disabled={processing}
            variant="contained"
            onClick={handleRefresh}
            data-testid={tid('refresh', 'button')}
          >
            Refresh
          </Button>
        </Tooltip>
        {!!unprocessedQuestion && (
          <Tooltip title="Apply changes to downstream dependencies. This will regenerate LLM responses on later pages that depend on this one.">
            <Button disabled={processing} variant="contained" onClick={() => setOpenDialog(true)}>
              Apply Changes
            </Button>
          </Tooltip>
        )}
      </Box>
      <Divider sx={{ mb: 2 }} />
      {groupedBenchmarks.map(({ category, benchmarkQuestions }, i) => (
        <Box key={category} sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="subtitle2" data-testid={tid('category', i + 1)}>
              {category}
            </Typography>
            {i === 0 && (
              <Box sx={{ display: 'flex' }}>
                <FixedWidthTypography align="center">No Filter</FixedWidthTypography>
                {salesforce?.industry && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <FixedWidthTypography align="center">Industry:</FixedWidthTypography>
                    <Tooltip title={salesforce.industry}>
                      <TruncatedBlueText align="center" data-testid={tid('industry', 'header')}>
                        {salesforce.industry}
                      </TruncatedBlueText>
                    </Tooltip>
                  </Box>
                )}
                {salesforce?.subIndustry && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <FixedWidthTypography align="center">Subindustry:</FixedWidthTypography>
                    <Tooltip title={salesforce.subIndustry}>
                      <TruncatedBlueText align="center" data-testid={tid('subindustry', 'header')}>
                        {salesforce.subIndustry}
                      </TruncatedBlueText>
                    </Tooltip>
                  </Box>
                )}
                {salesforce?.revenueBand && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <FixedWidthTypography align="center">Revenue Band:</FixedWidthTypography>
                    <Tooltip title={salesforce.revenueBand || 'No revenue band specified'}>
                      <TruncatedBlueText align="center" data-testid={tid('revenue-band', 'header')}>
                        {salesforce.revenueBand}
                      </TruncatedBlueText>
                    </Tooltip>
                  </Box>
                )}
              </Box>
            )}
          </Box>
          <Divider />
          {benchmarkQuestions.map((benchmarkQuestion) => (
            <Benchmark
              key={benchmarkQuestion.id}
              salesforce={salesforce}
              benchmarkQuestion={benchmarkQuestion}
              refetchQuestions={refetchQuestions}
            />
          ))}
        </Box>
      ))}
      {loading && <LoadingMask isLoading={loading} />}
      <ApplyChangesDialog
        open={openDialog}
        question={unprocessedQuestion}
        timeToProcess="5-10 minutes"
        onConfirm={handleConfirmApply}
        onClose={() => setOpenDialog(false)}
      />
    </Box>
  );
};

export default BenchmarkPage;
